/**
 * ──────────────────────────────────────────────────────────────────────────
 *  Response Level — effective / escalation-safe math (#445-ws2, migration 0148).
 *
 *  The SINGLE source of the two-axis Response Level reconciliation shared by the
 *  web (queue converters, fire disc, incident badge) and the api (feature-overlay
 *  join, SITREP fold, incident-detail read). Pure functions — no DB, no React.
 *
 *  Ontario IMS §5 Table 1 frames the Response Level (Small/Large/Major/Emergency,
 *  1..4) as the responder's professional judgment ("make a decision to adjust the
 *  scale"); §6.1 gives the Incident Commander that authority. So the COP carries
 *  BOTH:
 *    • the SYSTEM-DERIVED level (the per-hazard ladder / incident max-fold), and
 *    • the OPERATOR-ASSESSED level the IC sets on the incident (records-not-confers).
 *  These are NEVER conflated into one column — the derived is always preserved.
 *
 *  TWO reconciled readings, one for each kind of surface:
 *
 *  1. EFFECTIVE (the OPERATIVE level) = `operator ?? derived`.
 *     What the IC asserts is the incident's Response Level. Drives the incident
 *     detail badge, shown ALONGSIDE the annotated system-derived (honesty).
 *
 *  2. ESCALATION-SAFE (the never-bury level) = the HIGHER of the operative and the
 *     system-derived. Drives every LIFE-SAFETY surface — the urgent-queue gate,
 *     the queue rank/sort, the map disc, and the SITREP header fold.
 *       • an IC ESCALATION (operator > derived) LIFTS these surfaces — the COALESCE
 *         effect the founder asked for (RL3-over-derived-RL2 reaches the urgent queue).
 *       • an IC DE-ESCALATION (operator < derived) can NEVER lower them below the live
 *         system-derived — the founder's de-escalation guard: a manual de-escalation
 *         must not silently bury a worsening hazard. The operator's lower assessment
 *         only governs the safety surfaces once the system agrees the hazard has
 *         calmed (derived drops to/below the operator level).
 *
 *  Pure `operator ?? derived` (COALESCE) cannot satisfy BOTH the "escalation reaches
 *  the queue" and "de-escalation never buries a worse derived" constraints — the
 *  escalation-safe max is the reconciliation. (This is the CTO reading of the two
 *  founder constraints; the "Response Level = severity vs §5 structural" semantics
 *  fork remains a separate founder ruling.)
 * ──────────────────────────────────────────────────────────────────────────
 */

/** The valid operator-assessed / derived Response Levels (Ontario IMS §5 Table 1). */
export type ResponseLevelValue = 1 | 2 | 3 | 4;

/** Ontario IMS §5 Table 1 Response Level TIER NAMES (1..4). The SINGLE backend source of
 *  the §5 names, so the API dual-display surfaces (the #620 SITREP header / PDF / email)
 *  never re-declare them out of lockstep. The web mirrors these in RESPONSE_LEVEL_META
 *  (apps/web/src/styles/responseLevel.ts) — pinned equal by threatResponseAxis.lock.test.tsx.
 *  These are the §5 RESPONSE axis, never the machine THREAT axis (Low/Moderate/High/Very High). */
export const RESPONSE_LEVEL_TIER_NAME: Record<ResponseLevelValue, string> = {
  1: 'Small',
  2: 'Large',
  3: 'Major',
  4: 'Emergency',
};

/** The OPERATIVE Response Level (`operator ?? derived`) — what the IC asserts.
 *  Drives the incident badge (shown next to the annotated system-derived). Returns
 *  null only when BOTH are null. */
export function effectiveResponseLevel(
  operatorSet: number | null | undefined,
  derived: number | null | undefined,
): number | null {
  return operatorSet ?? derived ?? null;
}

/** The ESCALATION-SAFE Response Level — the higher of the operative and the
 *  system-derived. Drives the urgent-queue gate, rank, map disc, and SITREP fold so
 *  an IC escalation LIFTS them while an IC de-escalation can never bury a worse
 *  live system-derived level (the de-escalation guard). Returns null only when BOTH
 *  are null. */
export function escalationSafeResponseLevel(
  operatorSet: number | null | undefined,
  derived: number | null | undefined,
): number | null {
  const op = operatorSet ?? null;
  const dv = derived ?? null;
  if (op == null) return dv;
  if (dv == null) return op;
  return Math.max(op, dv);
}

/** True when the SYSTEM-DERIVED level EXCEEDS the operator's pinned level — the
 *  divergence a de-escalation must surface prominently (the worsening hazard the IC
 *  pinned below). False when no operator level is set or the two do not diverge upward. */
export function derivedExceedsOperator(
  operatorSet: number | null | undefined,
  derived: number | null | undefined,
): boolean {
  return operatorSet != null && derived != null && derived > operatorSet;
}

/** The operator-facing action WORD for a Set transition (founder-sanctioned verbs,
 *  #445-ws2): 'Set' from unset (or a re-affirm at the same level), 'Escalate' when
 *  raising, 'De-escalate' when lowering. NEVER "Declare" (a declaration of emergency
 *  is a distinct doctrine concept). Backend verb stays `set_incident_response_level`. */
export function responseLevelActionWord(
  fromLevel: number | null | undefined,
  toLevel: number,
): 'Set' | 'Escalate' | 'De-escalate' {
  if (fromLevel == null) return 'Set';
  if (toLevel > fromLevel) return 'Escalate';
  if (toLevel < fromLevel) return 'De-escalate';
  return 'Set';
}
